import React from 'react';
import PropTypes from 'prop-types';
import { darken } from 'polished';
import { css } from '@emotion/core';
import {
  mediaBreakpointDownSM,
  ssColorCyan,
  ssColorWhite,
} from '../constants/style-constants';

const formatRowColour = noOfRows => [...Array(noOfRows).keys()].map((key, index) => {
  const darknessMultiplier = 0.6;
  const darkenAmount = (index + 1) / noOfRows * darknessMultiplier;
  return `&:nth-of-type(${index + 1}) > div {
    background: ${darken(darkenAmount, ssColorCyan)};
  }`;
});

const chartCss = css`
  a {
    color: ${ssColorWhite};
  }

  ${mediaBreakpointDownSM} {
    margin-left: -1.5rem;
    margin-right: -1.5rem;
    font-size: 0.9rem;

    .role-match {
      padding-left: 1rem;
    }
  }
`;

const rowItemCss = noOfRows => css`
  color: white;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  position: relative;

  & > div {
    display: flex;
    justify-content: space-between;
    position: relative;
  }

  ${formatRowColour(noOfRows)}
`;

const rowItemLabelCss = css`
  ${mediaBreakpointDownSM} {
    padding-left: 2rem;
  }
  padding: 0.8rem;
  font-weight: bold;
  white-space: nowrap;
`;

const rowItemValueCss = css`
  ${mediaBreakpointDownSM} {
    padding-left: 2rem;
  }
  padding: 0.8rem;
  font-weight: bold;
  white-space: nowrap;
  text-transform: uppercase;
`;

const formatAsPercentage = (ceil, val) => `${val / ceil * 100}%`;

const formatWidth = (ceil, val) => ({ width: formatAsPercentage(ceil, val) });

const HorizontalBarChart = ({
  rows,
  formatValue,
  ceilingValue,
  ...restProps
}) => (
  <div {...restProps} css={chartCss}>
    {
      rows.map(({
        link,
        label,
        value,
      }) => {
        const content = (
          <div style={formatWidth(ceilingValue, value)}>
            <div css={rowItemLabelCss}>{label}</div>
            <div css={rowItemValueCss}>
              {formatValue(ceilingValue, value)}
            </div>
          </div>
        );
        return link
          ? <a href={link} key={label} css={rowItemCss(rows.length)}>{content}</a>
          : <div css={rowItemCss(rows.length)} key={label}>{content}</div>;
      })
    }
  </div>
);

const barItemProps = PropTypes.shape({
  label: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  link: PropTypes.string,
});

HorizontalBarChart.propTypes = {
  rows: PropTypes.arrayOf(barItemProps).isRequired,
  ceilingValue: PropTypes.number.isRequired,
  formatValue: PropTypes.func,
};

HorizontalBarChart.defaultProps = {
  formatValue: (ceil, val) => val,
};

export default HorizontalBarChart;
